import request from 'utils/request';
import { saveAs } from 'file-saver';
import { useState } from 'react';
import { useSchoolSchoolyear } from 'containers/App/context/SchoolyearSchoolProvider';
import parse from 'utils/time/parse';
import { format } from 'utils/time/format';
import { useCJConfigurationDisplay } from 'containers/CahierJournal/useCJConfigurationDisplay';
import {
  isoDatePattern,
  isoDatePatternForFilename,
} from 'utils/time/datePatterns';
import { useCollapsedDays } from 'containers/CahierJournal/atoms';
import { CahierJournalView } from 'containers/CahierJournal/types';
import { CJPDFSettings, PrintCJDirection } from 'types/cj';
import { useFieldsToDisplay } from 'containers/CahierJournal/Toolbar/Filters/FieldsToDisplay/atom';
import { Field } from 'containers/CahierJournal/Toolbar/Filters/FieldsToDisplay/types';
import useNotification from 'containers/App/Notifications/useNotification';
import { useTranslation } from 'react-i18next';
import { PdfResponse } from 'types/pdf';
import { buildCJPDFParams } from 'containers/CahierJournal/Preview/Sidebar/buildCJPDFParams';

interface Props {
  classroomsId: string[];
  schoolyearId: string;
  schoolId: string;
  onStart: () => void;
  onFinished: () => void;
  onError: () => void;
  day: string;
  view: CahierJournalView;
  direction?: PrintCJDirection;
  collapsedDays: string[];
  fieldsToDisplay: Field[];
  hideEvents: boolean;
  settings: CJPDFSettings;
  lng: string;
  tz: string;
}

const fetchCJAPI = (s3Url: string, date: string, view: string) => {
  const d = parse(date, isoDatePattern);
  const dateParsed = format(d, 'fr-FR', isoDatePatternForFilename);

  return fetch(s3Url)
    .then(response => response.blob())
    .then(response => {
      saveAs(
        response,
        view === 'day'
          ? `${dateParsed}_cahier_journal.pdf`
          : `${dateParsed}_cahier_journal_semaine.pdf`,
      );
    });
};

export async function downloadCJ(props: Props) {
  const {
    schoolyearId,
    schoolId,
    onStart,
    onFinished,
    onError,
    day,
    view,
    direction,
    collapsedDays,
    classroomsId,
    settings,
    fieldsToDisplay,
    hideEvents,
    lng,
    tz,
  } = props;

  onStart();

  const params = buildCJPDFParams({
    lng,
    tz,
    classroomsId,
    collapsedDays,
    fieldsToDisplay,
    hideEvents,
    settings,
    view,
  });
  const requestURL = `/schoolyears/${schoolyearId}/schools/${schoolId}/cahier-journal/export/pdf?${params.toString()}`;
  try {
    const response: PdfResponse = await request(requestURL, {
      method: 'POST',
      body: { day, view, classroomsId, collapsedDays, direction },
    });
    if ((window as any)?._isPlaywright) {
      (window as any).cjPrintedUrl = response.printUrl;
    }
    const token = response.pdf_token;
    await fetchCJAPI(token, day, view);
  } catch {
    onError();
  } finally {
    onFinished();
  }
}

export function useDownloadSexyCJ() {
  const { classroomsId, hideEvents } = useCJConfigurationDisplay();
  const { schoolyear, school } = useSchoolSchoolyear();
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const collapsedDays = useCollapsedDays();
  const fieldsToDisplay = useFieldsToDisplay();
  const { notifyError } = useNotification();
  const { t } = useTranslation(['common']);

  const download = ({
    day,
    view,
    direction,
    settings,
    lng,
    tz,
  }: {
    day: string;
    view: CahierJournalView;
    direction?: PrintCJDirection;
    settings: CJPDFSettings;
    lng: string;
    tz: string;
  }) =>
    downloadCJ({
      day,
      view,
      direction,
      collapsedDays,
      classroomsId,
      fieldsToDisplay,
      hideEvents,
      settings,
      lng,
      tz,
      schoolyearId: schoolyear.id,
      schoolId: school.id,
      onStart: () => setIsDownloading(true),
      onFinished: () => setIsDownloading(false),
      onError: () => notifyError({ title: t('common:messages.genericError') }),
    });

  return {
    download,
    isDownloading,
  };
}
